<template>
  <div :class="['item', { me: message.sender === 'Me' }]">
    <div class="name desc">{{ message.sender }}</div>

    <div
      v-if="message.message_type === 'text'"
      :class="[
        'message desc',
        { typing: message.is_typing },
        { empty: !message.content && !message.is_typing },
      ]"
    >
      {{ message.is_typing ? "Typing..." : message.content }}
    </div>

    <div
      v-else-if="message.message_type === 'sticker' && message.sticker"
      class="sticker-message"
    >
      <img
        :src="message.sticker.image_url"
        :alt="message.sticker.name"
        class="sticker-img"
      />
    </div>

    <div
      v-else-if="
        message.message_type === 'sticker_pack_gift' &&
        message.sticker_pack_gift
      "
      class="gift-message"
    >
      <div v-if="message.is_my_message" class="gift-sent">
        <img
          v-if="message.sticker_pack_gift.status === 'claimed'"
          src="../assets/tick.svg"
          alt="gift icon"
          class="gift-icon"
          :class="{ claimed: message.sticker_pack_gift.status === 'claimed' }"
        />
        <img
          v-else
          src="../assets/gift.svg"
          alt="gift icon"
          class="gift-icon"
          :class="{ claimed: message.sticker_pack_gift.status === 'claimed' }"
        />
        <div
          class="gift-content"
          :class="{ claimed: message.sticker_pack_gift.status === 'claimed' }"
        >
          <div class="gift-text">
            <p v-if="message.sticker_pack_gift.status === 'pending'">
              I give you this sticker pack!
            </p>
            <p v-else-if="message.sticker_pack_gift.status === 'claimed'">
              Sticker pack claimed
            </p>
            <p v-else>Sticker pack rejected</p>
          </div>

          <img
            :src="message.sticker_pack_gift.sticker_pack.image_url"
            alt="sticker pack preview"
            class="gift-preview"
          />
        </div>
      </div>
      <div v-else class="gift-received">
        <div
          class="gift-content"
          :class="{ claimed: message.sticker_pack_gift.status === 'claimed' }"
        >
          <div class="gift-text">
            <p>I give you this sticker pack!</p>
          </div>
          <img
            :src="message.sticker_pack_gift.sticker_pack.image_url"
            alt="sticker pack preview"
            class="gift-preview"
          />
          <div
            v-if="message.sticker_pack_gift.status === 'claimed'"
            class="claimed-indicator"
          >
            <div class="green-border"></div>
            <img src="../assets/tick.svg" alt="claimed" class="tick-icon" />
          </div>
        </div>
        <img
          src="../assets/gift.svg"
          alt="gift icon"
          class="gift-icon"
          :class="{ claimed: message.sticker_pack_gift.status === 'claimed' }"
        />
      </div>
    </div>

    <div class="message-img-list">
      <img
        v-for="{ image_url } in getImageAttachments(message.attachments)"
        :key="image_url"
        @click="imagePreview(image_url)"
        class="message-img"
        :src="image_url"
      />
    </div>

    <div class="message-doc-list">
      <div
        v-for="{ original_filename, path, filesize } in getDocumentAttachments(
          message.attachments
        )"
        :key="path"
        class="doc message"
      >
        <img
          class="doc-img"
          @click="openOrDownloadFile(path)"
          src="../assets/doc2.svg"
        />
        <div class="text">
          <div class="desc">{{ original_filename }}</div>
          <div v-if="filesize" class="desc">{{ humanFileSize(filesize) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { humanFileSize } from "@/utils";

export default {
  name: "MessageItem",
  props: {
    message: {
      type: Object,
      required: true,
    },
  },
  methods: {
    humanFileSize,
    openOrDownloadFile(path) {
      this.$emit("open-file", path);
    },
    imagePreview(path) {
      this.$emit("preview-image", path);
    },
    getImageAttachments(attachments) {
      return attachments.filter(({ type }) => type === "image");
    },
    getDocumentAttachments(attachments) {
      return attachments.filter(({ type }) => type === "document");
    },
  },
};
</script>

<style scoped>
.sticker-message {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
}

.sticker-img {
  width: auto;
  height: 172px;
}

.gift-message {
  color: #fff;
  display: flex;
  flex-direction: column;
}

.gift-sent,
.gift-received {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 5px;
}

.gift-sent {
  flex-direction: row;
}

.gift-received {
  flex-direction: row-reverse;
}

.gift-icon {
  width: 36px;
  height: 36px;
  padding: 8px;
  border-radius: 8px;
  flex-shrink: 0;
  border-width: 1px;
  border-style: solid;
}

.gift-sent .gift-icon {
  border-color: #ed59e3;
  filter: brightness(0) saturate(100%) invert(53%) sepia(45%) saturate(2963%)
    hue-rotate(274deg) brightness(96%) contrast(93%);
}

.gift-received .gift-icon {
  border-color: #28a745;
}

.gift-icon.claimed {
  border-color: #58e03d;
  filter: brightness(0) saturate(100%) invert(97%) sepia(100%) saturate(2461%)
    hue-rotate(44deg) brightness(94%) contrast(86%);
}

.gift-content {
  min-width: 216px;
  display: flex;
  flex-direction: column;
  border: 1px dashed;
  padding: 14px 16px 14px 24px;
  border-radius: 10px 2px 10px 10px;
  gap: 10px;
  background-color: #0e0e12;
  position: relative;
}

.gift-content.claimed {
  border-style: solid;
}

.gift-sent .gift-content {
  border-color: #ed59e3;
}

.gift-received .gift-content {
  border-color: #28a745;
}

.gift-sent .gift-content.claimed {
  border-color: transparent;
}

.gift-received .gift-content.claimed {
  border-color: transparent;
}

.gift-text {
  flex: 1;
}

.gift-text p {
  margin: 0;
  font-size: 14px;
}

.gift-preview {
  background: #1d1d24;
  max-width: 64px;
  max-height: 64px;
  border: 2px solid #ed59e3;
  border-radius: 14px;
  padding: 7px;
}

.claimed-indicator {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 10px 2px 10px 10px;
  overflow: hidden;
}

.green-border {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 2px solid #28a745;
  border-radius: 10px 2px 10px 10px;
  box-sizing: border-box;
}

.tick-icon {
  width: 24px;
  height: 24px;
  z-index: 1;
  filter: brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(7500%)
    hue-rotate(240deg) brightness(108%) contrast(101%);
}
</style>
